<script setup lang="ts">
import { useAuthStore } from "~/stores/auth/store";
import useLoginForm from "~/composables/forms/useLoginForm";
import { first, map } from "lodash-es";
import PgInputText from "~/components/Inputs/PgInputText.vue";
import { Field } from "vee-validate";

definePageMeta({
  name: "pokerGO::page:login-page",
  middleware: ["no-auth"],
});

useSeoMeta({
  title: "Login | PokerGO",
  ogTitle: "Login | PokerGO",
  twitterTitle: "Login | PokerGO",
});

const route = useRoute();

const authStore = useAuthStore();

const { form } = useLoginForm();

const onSubmit = form.handleSubmit(async (values) => {
  try {
    await authStore.requestLogin({
      ...values,
      ott_code: route.query?.ott_code
        ? parseInt(route.query.ott_code as string)
        : undefined,
    });
  } catch (e: any) {
    form.setFieldError(
      "password",
      first(map(e.response._data.errors, "detail")),
    );
  }
});

const registerLink = computed(() => {
  if (route.query?.ott_code) {
    return `register?ott_code=${route.query?.ott_code}`;
  }
  return "/register/select-plan";
});
</script>

<template>
  <div>
    <div class="h-20"></div>
    <div class="m-auto max-w-screen-xl px-5 py-10">
      <div class="mb-2 text-center text-4xl font-bold">
        Welcome to your PokerGO viewing experience!
      </div>

      <div class="mb-10 text-center text-lg text-surface-300">
        Let’s create your account so you can start watching your favorite shows
        and streams and keep tabs on the best players in the world.
      </div>
      <div
        class="mx-auto mt-10 rounded-md border border-surface-border p-5 lg:w-[600px]"
      >
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-4">
            <PgInputText
              name="email"
              size="large"
              placeholder="Email Address"
              fluid
              :is-form-field="true"
              @keydown.enter="onSubmit"
            />

            <Field v-slot="{ field, errorMessage }" name="password">
              <PgInputPassword
                v-bind="field"
                :error="errorMessage"
                name="password"
                placeholder="Password"
                size="large"
                fluid
                @keydown.enter="onSubmit"
              />
            </Field>
          </div>

          <NuxtLink
            class="text-sm text-primary-200 underline"
            to="/forgot-password"
          >
            Forgot Password
          </NuxtLink>
          <div>
            <Button
              :loading="form.isSubmitting.value"
              label="Login"
              class="w-full"
              @click="onSubmit"
            />
          </div>
        </div>

        <div class="mt-5 text-center text-sm text-surface-300">
          Don’t have an account?
          <NuxtLink class="text-primary-200" :to="registerLink">
            Register
          </NuxtLink>
        </div>

        <div class="mt-5 text-center text-sm text-surface-300">
          Customer Service is available to help!<br />
          Email
          <a
            target="_blank"
            href="mailto:support@pokergo.com"
            class="text-primary-200 underline"
          >
            support@pokergo.com
          </a>
          or call us at 1-866-476-5374
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
